<script setup lang="ts">
import { api } from '@/api';
import {
  BeamQualityDto,
  ElectronBeamQualitiesDto,
  EnumerationValueDto,
  instanceOfPhotonBeamQualitiesDto,
  LinearAcceleratorDto,
  OtherMvTherapyMachineDto,
  PhotonBeamQualitiesDto,
  PhotonBeamQualityDto,
  RadiationType,
} from '@/api/generator';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import { useFormMode } from '@/components/forms/useFormMode';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { OverviewTableProps, SortItem } from '@/components/OverviewTable/types';
import { truthy } from '@/utils/helper';
import { toDisplayValue } from '@/utils/numberUtils.ts';
import { I18nKey } from '@/utils/typings';
import { mdiPlus } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import { cloneDeep } from 'lodash';
import { FieldEntry, useFieldArray, useFieldValue, useFormValues } from 'vee-validate';
import { computed, ref, toRef } from 'vue';
import { TargetBeamQuality } from '../types';
import BeamQualityTableAddDialog from './BeamQualityTableAddDialog.vue';
import { NIL } from 'uuid';
import {
  alphabeticalStringCompare,
  cascadedCompareFunction,
  optionalNumberCompare,
} from '@/utils/compareAndSort';

const { t } = useTranslation();

const props = defineProps<{
  name: string;
  type: RadiationType;
}>();

async function addBeamQuality(radiationType: RadiationType) {
  const { result: beamQuality } = await api.medicalSystemsApi.getBeamQualityTemplate({
    radiationType,
  });

  if (beamQuality) {
    targetBeamQuality.value = { beamQuality };
  }
}

const formMode = useFormMode();
const medicalSystemValues = useFormValues<LinearAcceleratorDto | OtherMvTherapyMachineDto>();

const beamQualityItem = useFieldValue<PhotonBeamQualitiesDto | ElectronBeamQualitiesDto>(
  () => props.name,
);

const items = useFieldArray<BeamQualityDto>(toRef(() => `${props.name}.values`));

const initialBeamQualities = cloneDeep(items.fields.value);

const targetBeamQuality = ref<TargetBeamQuality>();

function confirm(beamQuality: BeamQualityDto, index?: number) {
  if (index === undefined) {
    items.prepend(beamQuality);
  } else {
    items.update(index, beamQuality);
  }
}

function cancel() {
  targetBeamQuality.value = undefined;
}

function enumerationValueDtoSortingFn(a: EnumerationValueDto, b: EnumerationValueDto) {
  const aValue = a.value?.name ?? '';
  const bValue = b.value?.name ?? '';
  return aValue.localeCompare(bValue);
}

const headers = computed<OverviewTableProps<BeamQualityDto, string>['headers']>(() => [
  {
    title: t('Name'),
    key: 'value.name',
  },
  {
    key: 'empty',
    title: t('SetupParameters'),
    align: 'center',
    sortable: false,
    children: [
      {
        title: t(beamQualityItem.value.radiationTypeLabel as I18nKey),
        key: 'value.radiationType',
        sortable: false,
      },

      {
        title: t(beamQualityItem.value.energyLabel as I18nKey),
        key: 'value.energy',
        align: 'end',
        sortRaw: cascadedCompareFunction<FieldEntry<BeamQualityDto>>([
          (a, b) => optionalNumberCompare(a.value.energy.value, b.value.energy.value),
          (a, b) => alphabeticalStringCompare(a.value.name, b.value.name),
        ]),
      },

      ...(instanceOfPhotonBeamQualitiesDto(beamQualityItem.value)
        ? [
            {
              title: t(
                (beamQualityItem.value as PhotonBeamQualitiesDto)
                  .primaryFluenceShapingLabel as I18nKey,
              ),
              key: 'value.primaryFluenceShaping',
              sort: enumerationValueDtoSortingFn,
            },
          ]
        : []),

      {
        title: t(beamQualityItem.value.highDoseTechniqueTypeLabel as I18nKey),
        key: 'value.highDoseTechniqueType',
        sort: enumerationValueDtoSortingFn,
      },
    ],
  },
  {
    title: t('AvailableDoseRatesForStaticBeams'),
    align: 'end',
    sortable: false,
    key: 'value.availableDoseRates',
  },
  ...(formMode.value !== 'details' ? [globalContextMenuHeaderItemDefinition] : []),
]);

function getContextMenuActions(item: BeamQualityDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions();
  const beamQualityDtos = items.fields.value.map((i) => i.value);
  const myIndex = beamQualityDtos.indexOf(item);

  return [
    {
      ...defaultContextMenuActions.edit,
      callback: async () => {
        targetBeamQuality.value = {
          beamQuality: item,
          initialBeamQuality: initialBeamQualities.find((i) => i.value.id === item.id)?.value,
          index: myIndex,
        };
      },
    },
    {
      ...defaultContextMenuActions.delete,
      disabled: medicalSystemValues.value.isReferenced && item.id !== NIL,
      tooltip:
        medicalSystemValues.value.isReferenced && item.id !== NIL
          ? t('DeleteBeamQualityDisabledReasonTooltipMsg')
          : undefined,
      callback: async () => {
        items.remove(myIndex);
      },
    },
  ];
}

function getDoseRatesString(
  values: number[] | null | undefined,
  unit: string | null | undefined,
): string {
  if (!values || !unit || values.length === 0) {
    return EMPTY_FIELD_SIGN;
  }
  return `${values?.join(' / ')} ${unit}`;
}

const sorting = ref<SortItem[]>([{ key: 'value.energy', order: 'asc' }]);
</script>

<template>
  <div>
    <BeamQualityTableAddDialog
      :target="targetBeamQuality"
      :ownerIsInUse="medicalSystemValues.isReferenced"
      @confirm="confirm"
      @cancel="cancel"
    />
    <div class="mt-8 d-flex">
      <h6>
        {{
          $t(
            props.type === RadiationType.Electron
              ? 'AvailableElectronBeamQualities'
              : 'AvailablePhotonBeamQualities',
          )
        }}
      </h6>
      <template v-if="formMode !== 'details'">
        <VSpacer />
        <TextVBtn :prependIcon="mdiPlus" size="x-small" @click="addBeamQuality(props.type)"
          >{{
            $t(props.type === RadiationType.Electron ? 'ElectronBeamQuality' : 'PhotonBeamQuality')
          }}
        </TextVBtn>
      </template>
    </div>
    <p
      v-if="items.fields.value?.length === 0 && formMode === 'details'"
      class="text-body-1 font-weight-semibold text-primary-lighten-2 mt-4"
    >
      {{
        $t(
          props.type === RadiationType.Electron
            ? 'NoElectronBeamQualitiesAvailable'
            : 'NoPhotonBeamQualitiesAvailable',
        )
      }}
    </p>

    <OverviewTable
      v-if="
        items.fields.value?.length || (formMode !== 'details' && items.fields.value?.length === 0)
      "
      :sortBy="sorting"
      :headers="headers?.filter(truthy)"
      :items="items.fields.value"
      :data-testid="`DataTable_BeamQuality_${type}`"
      :zebra="true"
      :noSpacing="true"
      :no-data-label="
        $t(
          props.type === RadiationType.Electron
            ? 'NoElectronBeamQualitiesAvailable'
            : 'NoPhotonBeamQualitiesAvailable',
        )
      "
    >
      <template #item.value.name="{ item }">
        {{ item.value.name }}
      </template>

      <template #item.value.radiationType="{ item }">
        {{ $t(item.value.radiationType.value?.name as I18nKey) }}
      </template>

      <template #item.value.energy="{ item }">
        {{
          toDisplayValue(item.value.energy.value, {
            fractionDigits: item.value.energy.decimalPlaces,
            unitAbbreviation: item.value.energy.unitAbbreviation,
            valueType: item.value.energy.valueType,
          })
        }}
      </template>

      <template v-if="type === 'Photon'" #item.value.primaryFluenceShaping="{ item }">
        {{
          (item.value as PhotonBeamQualityDto).primaryFluenceShaping?.value?.name
            ? $t((item.value as PhotonBeamQualityDto).primaryFluenceShaping?.value?.name as I18nKey)
            : EMPTY_FIELD_SIGN
        }}
      </template>

      <template #item.value.highDoseTechniqueType="{ item }"
        >{{
          item.value.highDoseTechniqueType?.value?.name
            ? $t(item.value.highDoseTechniqueType?.value?.name as I18nKey)
            : EMPTY_FIELD_SIGN
        }}
      </template>

      <template #item.value.availableDoseRates="{ item }"
        >{{
          getDoseRatesString(
            item.value.availableDoseRates?.values,
            item.value.availableDoseRates?.unitAbbreviation,
          )
        }}
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu v-if="formMode !== 'details'" :actions="getContextMenuActions(item.value)" />
      </template>
    </OverviewTable>
  </div>
</template>
